import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import api from '../services/api';
import { format, startOfMonth, endOfMonth, subMonths, subQuarters, startOfYear, endOfYear } from 'date-fns';
import './RequiredCashReconciliationPage.css';
import { parseISO, isValid } from 'date-fns';

const RequiredCashReconciliationPage = () => {
  const { businessId } = useParams();

  const [plaidCategories, setPlaidCategories] = useState([]);
  const [selectedCategoryId, setSelectedCategoryId] = useState('');
  const [existingMatchId, setExistingMatchId] = useState(null);
  const [requiredCashByDate, setRequiredCashByDate] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [isEditing, setIsEditing] = useState(false);

  const [periodOption, setPeriodOption] = useState('month');
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');
  const [selectedDates, setSelectedDates] = useState([]);
  // near other useState calls
const [selectedLinkedTxId, setSelectedLinkedTxId] = useState(null);
// which RC date row is expanded
const [expandedRCDate, setExpandedRCDate] = useState(null);
// cache for RC->Plaid link details by date
const [rcLinkByDate, setRcLinkByDate] = useState({});

// which Plaid tx row is expanded
const [expandedTxId, setExpandedTxId] = useState(null);
// cache for Plaid->RC link details by tx id
const [txLinkById, setTxLinkById] = useState({});

const [loadingRCDate, setLoadingRCDate] = useState({}); // { [date]: true|false }
const [loadingTxId, setLoadingTxId] = useState({});     // { [txId]: true|false }
const [highlightedRCDates, setHighlightedRCDates] = useState(new Set());

const [linkedSumByTx, setLinkedSumByTx] = useState({}); // { [txId]: number }






  const computeDateRange = useCallback(() => {
    const today = new Date();
    let start_date, end_date;

    switch (periodOption) {
      case 'previous-month':
        start_date = format(startOfMonth(subMonths(today, 1)), 'yyyy-MM-dd');
        end_date = format(endOfMonth(subMonths(today, 1)), 'yyyy-MM-dd');
        break;
      case 'previous-quarter':
        start_date = format(startOfMonth(subQuarters(today, 1)), 'yyyy-MM-dd');
        end_date = format(endOfMonth(subQuarters(today, 1)), 'yyyy-MM-dd');
        break;
      case 'year':
        start_date = format(startOfYear(today), 'yyyy-MM-dd');
        end_date = format(endOfYear(today), 'yyyy-MM-dd');
        break;
      case 'custom':
        start_date = customStart;
        end_date = customEnd;
        break;
      default:
        start_date = format(startOfMonth(today), 'yyyy-MM-dd');
        end_date = format(endOfMonth(today), 'yyyy-MM-dd');
    }

    return { start_date, end_date };
  }, [periodOption, customStart, customEnd]);

  const fetchCategories = useCallback(async () => {
    try {
      const res = await api.get('/categories', { params: { business_id: businessId } });
      setPlaidCategories(res.data || []);
    } catch (err) {
      console.error('Failed to fetch categories:', err);
    }
  }, [businessId]);

 




  const fetchTransactions = useCallback(async (categoryId) => {
  try {
    const res = await api.get('/plaid-transactions', {
      params: { business_id: businessId, category_id: categoryId }
    });
    const txs = res.data || [];
    setTransactions(txs);

    const summaries = {};
   await Promise.all(
     txs.map(async (t) => {
       try {
         const { data } = await api.get('/required-cash-link-for-tx', {
           params: { business_id: businessId, plaid_id: t.id }
         });
         const total = Array.isArray(data?.rc_rows)
           ? data.rc_rows.reduce((s, r) => s + toNum(r.amount), 0)
           : 0;
         summaries[t.id] = total;
       } catch {
         summaries[t.id] = 0;
       }
     })
   );
   setLinkedSumByTx(summaries);
  } catch (err) {
    console.error('Failed to fetch transactions:', err);
    setTransactions([]);
    setLinkedSumByTx({});
  }
}, [businessId]); // <-- added loadRCDeltas here


  const fetchCategoryMatch = useCallback(async () => {
    try {
      const res = await api.get('/required-cash-category-match', {
        params: { business_id: businessId, cash_category_name: 'Required Cash' }
      });
      if (res.data && res.data.plaid_category_id) {
        setSelectedCategoryId(res.data.plaid_category_id);
        setExistingMatchId(res.data.id);
        fetchTransactions(res.data.plaid_category_id);
      }
    } catch (err) {
      console.error('Failed to fetch category match:', err);
    }
  }, [businessId, fetchTransactions]);

  const fetchRequiredCash = useCallback(async () => {
  try {
    const { start_date, end_date } = computeDateRange();
    const { data } = await api.get('/required-cash-by-date', {
      params: { business_id: businessId, start_date, end_date }
    });

    setRequiredCashByDate(
      (data || []).map(r => ({
        id: r.id,
        date: r.date,
        requiredCash: r.required_cash,
        reconciled: !!r.rc_reconciled,           // <-- drives green check
        bank_transaction_id: r.bank_transaction_id,
        plaid: r.plaid || null,                  // optional: has plaid reconcile too
      }))
    );
  } catch (err) {
    console.error('Failed to fetch required cash:', err);
  }
}, [businessId, computeDateRange]);





// replace your existing helper with this
const findTxByBankId = (bankId) => {
  if (bankId == null) return null;
  const id = String(bankId);
  return (
    transactions.find(t => String(t.id) === id) ||
    transactions.find(t => String(t.pending_transaction_id || '') === id) ||
    null
  );
};




  useEffect(() => {
    fetchCategories();
    fetchCategoryMatch();
    fetchRequiredCash();
  }, [fetchCategories, fetchCategoryMatch, fetchRequiredCash]);

  const handleSaveMatch = async () => {
    try {
      if (existingMatchId) {
        await api.put(`/api/required-cash-category-match/${existingMatchId}`, {
          business_id: businessId,
          selected_category_id: selectedCategoryId
        });
      } else {
        const res = await api.post('/required-cash-category-match', {
          business_id: businessId,
          selected_category_id: selectedCategoryId
        });
        setExistingMatchId(res.data.id);
      }

      setIsEditing(false);
      fetchTransactions(selectedCategoryId);
      alert('Category match saved successfully!');
    } catch (err) {
      console.error('Failed to save category match:', err);
      alert('Failed to save match.');
    }
  };

  const handleDateSelection = (date) => {
  setSelectedDates(prev =>
    prev.includes(date) ? prev.filter(d => d !== date) : [...prev, date]
  );
};

const DISPLAY_FMT = 'MM/dd/yyyy'; // or 'MMM d, yyyy' if you prefer
const fmtDate = (value, pattern = DISPLAY_FMT) => {
  if (!value) return '';
  // Prefer ISO parsing for "YYYY-MM-DD" / "YYYY-MM-DDTHH:mm:ssZ" strings
  const parsed =
    typeof value === 'string'
      ? parseISO(value) // safe for "YYYY-MM-DD" too
      : value instanceof Date
        ? value
        : new Date(value);

  return isValid(parsed) ? format(parsed, pattern) : String(value);
};


const selectedDatesTotal = useMemo(() => {
  if (!selectedDates.length) return 0;
  const map = new Map(requiredCashByDate.map(r => [r.date, Number(r.requiredCash || 0)]));
  return selectedDates.reduce((sum, d) => sum + (map.get(d) || 0), 0);
}, [selectedDates, requiredCashByDate]);



// replace your current toggleExpandRC with this
const toggleExpandRC = async (row) => {
  if (!row?.reconciled) return;

  const isSame = expandedRCDate === row.date;
  const next = isSame ? null : row.date;
  setExpandedRCDate(next);

  if (!next) return;

  // If we already cached this date, still sync the right table highlight/expand
  if (rcLinkByDate[next]) {
    const plaid = rcLinkByDate[next]?.plaid;
    if (plaid) {
      const tx =
        findTxByBankId(plaid.id) ||
        findTxByBankId(plaid.pending_transaction_id);
      if (tx) {
        setSelectedLinkedTxId(tx.id);
         // make sure the right panel opens
      } else {
        setSelectedLinkedTxId(null);
      }
    } else {
      setSelectedLinkedTxId(null);
    }
    return;
  }

  // Not cached → fetch, cache, then sync right table
  try {
    setLoadingRCDate(prev => ({ ...prev, [next]: true }));
    const { data } = await api.get('/required-cash-link-for-date', {
      params: { business_id: businessId, date: next }
    });
    setRcLinkByDate(prev => ({ ...prev, [next]: data || {} }));

    const plaid = data?.plaid;
    if (plaid) {
      const tx =
        findTxByBankId(plaid.id) ||
        findTxByBankId(plaid.pending_transaction_id);
      if (tx) {
        setSelectedLinkedTxId(tx.id);
        
      } else {
        setSelectedLinkedTxId(null);
      }
    } else {
      setSelectedLinkedTxId(null);
    }
  } finally {
    setLoadingRCDate(prev => {
      const { [next]: _, ...rest } = prev;
      return rest;
    });
  }
};


const toggleExpandTx = async (tx) => {
  const isSame = expandedTxId === tx.id;
  const next = isSame ? null : tx.id;
  setExpandedTxId(next);

  // CLEAR highlights if collapsing
  if (!next) {
    setHighlightedRCDates(new Set());
    return;
  }

  // If cached, use it to highlight immediately
  if (txLinkById[next]) {
    const cachedDates = (txLinkById[next].rc_rows || []).map(r => r.entry_date);
    setHighlightedRCDates(new Set(cachedDates));
    setSelectedLinkedTxId(tx.id);
    return;
  }

  try {
    setLoadingTxId(prev => ({ ...prev, [next]: true }));
    const { data } = await api.get('/required-cash-link-for-tx', {
      params: { business_id: businessId, plaid_id: next }
    });

    setTxLinkById(prev => ({ ...prev, [next]: data || {} }));

    const dates = (data?.rc_rows || []).map(r => r.entry_date);
    setHighlightedRCDates(new Set(dates));     // <<< highlight here
    setSelectedLinkedTxId(tx.id);
  } catch (err) {
    console.error('toggleExpandTx error:', err);
    setHighlightedRCDates(new Set());
  } finally {
    setLoadingTxId(prev => {
      const { [next]: _, ...rest } = prev;
      return rest;
    });
  }
};



// put near other helpers
const sumLinkedForTx = (tx) => {
  if (!tx) return 0;
  const tid = String(tx.id);
  const pid = String(tx.pending_transaction_id || '');

  return requiredCashByDate
    .filter(r =>
      String(r.bank_transaction_id || '') === tid ||
      (pid && String(r.bank_transaction_id || '') === pid)
    )
    .reduce((s, r) => s + Number(r.requiredCash || 0), 0);
};



  


// normalize to YYYY-MM-DD (avoid Z/UTC issues)
const toYMD = (d) => {
  const dt = d instanceof Date ? d : new Date(d);
  return dt.toISOString().slice(0, 10);
};

const matchSelectedDatesToTx = async (tx) => {
  if (!selectedDates.length) {
    alert('Select one or more dates on the left first.');
    return;
  }

  // sum RC for selected dates from the left table state
  const totalRequiredCash = selectedDates.reduce((sum, date) => {
    const rc = requiredCashByDate.find(r => r.date === date)?.requiredCash || 0;
    return sum + Number(rc || 0);
  }, 0);

  const txAmount = Number(tx.amount);
  const diff = totalRequiredCash + txAmount;
  const statusMsg = diff === 0
    ? 'Perfect Match'
    : diff > 0
      ? `Short by $${diff.toFixed(2)}`
      : `Over by $${Math.abs(diff).toFixed(2)}`;

  const ok = window.confirm(
    `Selected Dates Total: $${totalRequiredCash.toFixed(2)}\n` +
    `Transaction Amount:  $${txAmount.toFixed(2)}\n` +
    `${statusMsg}\n\nProceed to reconcile?`
  );
  if (!ok) return;

  try {
    // IMPORTANT: backend expects YYYY-MM-DD
    const normDates = selectedDates.map(toYMD);

    await api.post('/required-cash-match', {
      business_id: businessId,
      plaid_transaction_id: tx.id,       // send the real id
      matched_dates: normDates,
      total_required_cash: totalRequiredCash,
      plaid_amount: txAmount,
      difference: diff
    });

    // refresh both tables
await fetchRequiredCash();
await fetchTransactions(selectedCategoryId);
setHighlightedRCDates(new Set(normDates)); // keep newly matched dates highlighted


// keep this tx selected/expanded
setSelectedLinkedTxId(tx.id);
setExpandedTxId(tx.id);

// 🔄 bust the cached link data for this tx, then refetch it so the
// expanded panel shows the newly linked RC dates
setTxLinkById(prev => {
  const { [tx.id]: _stale, ...rest } = prev;
  return rest;
});
const freshTxLink = await api.get('/required-cash-link-for-tx', {
  params: { business_id: businessId, plaid_id: tx.id }
});
setTxLinkById(prev => ({ ...prev, [tx.id]: freshTxLink.data || {} }));

// (Optional) if the currently expanded RC date was one of the matched dates,
// refresh that RC->Plaid link panel too so it shows the new tx immediately.
if (expandedRCDate && selectedDates.includes(expandedRCDate)) {
  const freshRCLink = await api.get('/required-cash-link-for-date', {
    params: { business_id: businessId, date: expandedRCDate }
  });
  setRcLinkByDate(prev => ({ ...prev, [expandedRCDate]: freshRCLink.data || {} }));
}

// clear manual selections now that they’re reconciled
setSelectedDates([]);

alert('Reconciled.');

  } catch (err) {
    console.error('matchSelectedDatesToTx error', err);
    alert('Failed to reconcile.');
  }
};


const toNum = (x) =>
  typeof x === 'number' ? x : parseFloat(String(x).replace(/[,$]/g, '')) || 0;



const unlinkRcFromTx = async (tx, rcRow) => {
  const ok = window.confirm(
    `Unlink ${rcRow.entry_date} — $${Number(rcRow.amount || 0).toFixed(2)} from this transaction?`
  );
  if (!ok) return;

  try {
    await api.post('/required-cash-unlink', {
      business_id: businessId,
      plaid_id: tx.id,          // canonical id
      rc_entry_id: rcRow.id     // we have this from link.rc_rows
    });

    // Refresh tables
    await fetchRequiredCash();
    await fetchTransactions(selectedCategoryId);

    // Keep this tx expanded and highlighted
    setSelectedLinkedTxId(tx.id);
    setExpandedTxId(tx.id);

    // Bust cache & refetch the expanded tx link details
    setTxLinkById(prev => {
      const { [tx.id]: _stale, ...rest } = prev;
      return rest;
    });
    const freshLink = await api.get('/required-cash-link-for-tx', {
      params: { business_id: businessId, plaid_id: tx.id }
    });
    setTxLinkById(prev => ({ ...prev, [tx.id]: freshLink.data || {} }));

    // Update left-side highlighting based on fresh links
    const freshDates = (freshLink.data?.rc_rows || []).map(r => r.entry_date);
    setHighlightedRCDates(new Set(freshDates));
  } catch (err) {
    console.error('unlinkRcFromTx error', err);
    alert('Failed to unlink.');
  }
};

const formatCatName = (name = "") =>
  name
    .replace(/[_-]+/g, " ")            // underscores/hyphens -> spaces
    .trim()
    .split(/\s+/)
    .map(w => (w ? w[0].toUpperCase() + w.slice(1).toLowerCase() : ""))
    .join(" ");


  const handleCategoryChange = (e) => {
    setSelectedCategoryId(e.target.value);
  };

  const handlePeriodChange = (e) => {
    setPeriodOption(e.target.value);
  };

  const handleCustomDateChange = (e) => {
    if (e.target.name === 'customStart') setCustomStart(e.target.value);
    else setCustomEnd(e.target.value);
  };

  return (
    <div className='req-cash-page'>
      <h2>Required Cash Reconciliation</h2>

      <div className='req-cash-cat'>
        <div>
          <label>Period: </label>
          <select value={periodOption} onChange={handlePeriodChange}>
            <option value="month">Current Month</option>
            <option value="previous-month">Previous Month</option>
            <option value="previous-quarter">Previous Quarter</option>
            <option value="year">Current Year</option>
            <option value="custom">Custom</option>
          </select>

          {periodOption === 'custom' && (
            <div style={{ marginTop: '10px' }}>
              <label>Start Date: </label>
              <input type="date" name="customStart" value={customStart} onChange={handleCustomDateChange} />
              <label style={{ marginLeft: '10px' }}>End Date: </label>
              <input type="date" name="customEnd" value={customEnd} onChange={handleCustomDateChange} />
              <button onClick={fetchRequiredCash} style={{ marginLeft: '10px' }}>Apply</button>
            </div>
          )}
        </div>

        <div className='cat-match-req'>
          {existingMatchId && !isEditing ? (
            <div>
              <p>
                <label>Matched Category:</label> {plaidCategories.find(c => c.id === Number(selectedCategoryId))?.name}
              </p>
              <button onClick={() => setIsEditing(true)}>Edit Selection</button>
            </div>
          ) : (
            <div>
              <label>Select Category to Match: </label>
              <select value={selectedCategoryId} onChange={handleCategoryChange}>
                <option value="">-- Select Category --</option>
                {plaidCategories.map(cat => (
                  <option key={cat.id} value={cat.id}>{formatCatName(cat.name)}</option>
                ))}
              </select>
              <button
                onClick={handleSaveMatch}
                disabled={!selectedCategoryId}
                style={{ marginLeft: '10px' }}
              >
                Save Match
              </button>
            </div>
          )}
        </div>
      </div>

      <div className='req-cash-tables'>
        <div className='req-cash-entry'>
          <h3>Required Cash by Date</h3>
          <table className='rc-table'>
            <thead>
              <tr>
              <th></th>
                <th>Date</th>
                <th>Required Cash</th>
              </tr>
            </thead>
            <tbody>
  {requiredCashByDate.map((row) => {
  const isExpanded = expandedRCDate === row.date;
  const link = rcLinkByDate[row.date];
  const isHighlighted = highlightedRCDates.has(row.date); // <<<

  return (
    <React.Fragment key={row.date}>
      <tr
  onClick={() => toggleExpandRC(row)}
  className={isHighlighted ? 'rc-row--highlight' : ''}
  style={{ cursor: row.reconciled ? 'pointer' : 'default' }}
  title={row.reconciled && row.bank_transaction_id ? `Linked to ${row.bank_transaction_id}` : ''}
>
        <td><input
            type="checkbox"
            checked={selectedDates.includes(row.date)}
            onChange={(e) => { e.stopPropagation(); handleDateSelection(row.date); }}
          /></td>
        <td className="is-detail">
          {" "}
          {fmtDate(row.date)}{" "}
          {row.reconciled && <span style={{ color: 'green', fontWeight: 700 }}>✅</span>}
        </td>
        <td><strong>${row.requiredCash.toFixed(2)}</strong></td>
      </tr>

      {isExpanded && (
        <tr>
          <td colSpan={3} style={{ background: '#fafafa' }}>
            {loadingRCDate[row.date] && <div>Loading linked transaction…</div>}
            {!loadingRCDate[row.date] && (!link || !link.plaid) && (
              <div style={{ color: '#a00' }}>No linked Plaid transaction found.</div>
            )}
            {!loadingRCDate[row.date] && link?.plaid && (
              <div style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
                <div><strong>Linked Tx:</strong> {link.plaid.name}</div>
                <div><strong>Date:</strong> {fmtDate(link.plaid.date)}</div>
                <div><strong>Amount:</strong> ${Math.abs(Number(link.plaid.amount || 0)).toFixed(2)}</div>
                
              </div>
            )}
          </td>
        </tr>
      )}
    </React.Fragment>
  );
})}


</tbody>

          </table>
          {selectedDates.length > 0 && (
  <div className="rc-selection-summary">
    <div className="rc-selection-summary__left">
      <strong>{selectedDates.length}</strong> {selectedDates.length === 1 ? 'date' : 'dates'} selected
    </div>
    <div className="rc-selection-summary__right">
      <span>Selected Total:</span>
      <strong>${selectedDatesTotal.toFixed(2)}</strong>
      <button
        className="rc-clear-btn"
        onClick={() => setSelectedDates([])}
        aria-label="Clear selected dates"
      >
        Clear
      </button>
    </div>
  </div>
)}

        </div>

        <div className='req-cash-plaid'>
          <h3>Matched Transactions</h3>
          {transactions.length > 0 ? (
            <table className='rc-table'>
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Description</th>
                  <th>Amount</th>
                  <th>Difference</th>

                </tr>
              </thead>
              <tbody>
  {transactions.map((tx) => {
    const isLinked = selectedLinkedTxId && (tx.id === selectedLinkedTxId);
    const isExpanded = expandedTxId === tx.id;
    const link = txLinkById[tx.id]; // { rc_rows, plaid }

    return (
      <React.Fragment key={tx.id}>
        <tr
  data-tx-id={tx.id}
  onClick={() => toggleExpandTx(tx)}
  className={isLinked ? 'tx-row--linked' : ''}
  style={{ cursor: 'pointer' }}
  title={isLinked ? 'Linked to selected reconciled date' : ''}

  >

          <td>{fmtDate(tx.date)}</td>
          <td>
  {tx.name}
  {(tx.is_reconciled === 1 || tx.is_reconciled === true) && (
    <span style={{ marginLeft: 6, color: 'green', fontWeight: 700 }}>✅</span>
  )}
</td>
 
 <td>${Math.abs(parseFloat(tx.amount)).toFixed(2)}</td>

<td>
  {(() => {
     const linked = linkedSumByTx[tx.id] ?? sumLinkedForTx(tx);
    const remaining = -(toNum(linked) + toNum(tx.amount)); // negative when over-matched
    const isZero = Math.abs(remaining) < 0.005;

    // red when negative (over), green when zero or positive (needs more RC)
    const badgeClass = isZero ? 'ok' : (remaining < 0 ? 'err' : 'ok');

    const sign = remaining < 0 ? '-' : '+';
    const display = isZero ? '$0.00' : `${sign}$${Math.abs(remaining).toFixed(2)}`;

    return <span className={`delta-badge ${badgeClass}`}>{display}</span>;
  })()}
</td>


        </tr>

        {isExpanded && (
  <tr>
    <td colSpan={4} className="is-detail">

      {loadingTxId[tx.id] ? (
        <div>Loading linked Required Cash…</div>
      ) : (
        <>
          {link && link.rc_rows?.length ? (
            <div>
              <div style={{ marginBottom: 6 }}>
                <strong>Linked Required Cash dates:</strong>
              </div>
              <ul style={{ margin: 0, paddingLeft: 18 }}>
  {link.rc_rows.map((r) => (
    <li key={r.entry_date} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <span>
        {fmtDate(r.entry_date)} — ${Number(r.amount || 0).toFixed(2)}
      </span>
      <button
        onClick={(e) => { e.stopPropagation(); unlinkRcFromTx(tx, r); }}
        title="Unlink"
        style={{
          border: 'none',
          background: 'transparent',
          cursor: 'pointer',
          color: '#b00',
          fontSize: 16,
          lineHeight: 1
        }}
        aria-label="Unlink"
      >
        🗑️
      </button>
    </li>
  ))}
</ul>

              <div style={{ marginTop: 6 }}>
                <strong>Total:</strong>{" "}
                ${link.rc_rows.reduce((s, r) => s + Number(r.amount || 0), 0).toFixed(2)}
              </div>
            </div>
          ) : (
            <div style={{ color: '#a00' }}>No Required Cash rows linked.</div>
          )}

          {/* <-- Always show the Match button when not loading */}
          <div style={{ marginTop: 8 }}>
            <button
              onClick={(e) => {
                e.stopPropagation();
                matchSelectedDatesToTx(tx);
              }}
            >
              Match selected dates to this transaction
            </button>
          </div>
        </>
      )}
    </td>
  </tr>
)}

      </React.Fragment>
    );
  })}
</tbody>

            </table>
          ) : (
            <p>No transactions found.</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default RequiredCashReconciliationPage;




